import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  getCollectionComponentTypeByName,
  addAllowedType,
  getCollectionComponentTypeById,
  getCollectionComponentTypesLookups,
  createCollectionComponentType,
  getCollectionComponentTypesDatatable,
  setAllowedComponentTypes,
  getCollectionComponentTypeByIdMultilingual,
  createCollectionComponentTypeMultilingual,
  editCollectionComponentTypeMultilingual,
} from './thunks';
import { CollectionComponentTypeState } from './types';

export const name = 'CollectionComponentTypes';

const initialState: CollectionComponentTypeState = {
  details: null,
  detailsStatus: 'idle',
  addAllowedType: {
    status: 'idle',
  },
  collectionComponentTypeById: {
    data: null,
    status: 'idle',
  },
  collectionComponentTypesLookups: {
    data: [],
    status: 'idle',
  },
  createCollectionComponentType: {
    data: null,
    status: 'idle',
  },
  collectionComponentTypesDatatable: {
    data: null,
    status: 'idle',
  },
  setAllowedComponentTypes: {
    status: 'idle',
  },
  collectionComponentTypeByIdMultilingual: {
    data: null,
    status: 'idle',
  },
  createCollectionComponentTypeMultilingual: {
    data: null,
    status: 'idle',
  },
  editCollectionComponentTypeMultilingual: {
    data: null,
    status: 'idle',
  },
};

export const getCollectionComponentTypeByNameAsync = createAsyncThunk(
  name + '/ getCollectionComponentTypeByName',
  getCollectionComponentTypeByName
);

export const getCollectionComponentTypeByIdAsync = createAsyncThunk(
  name + '/ getCollectionComponentTypeById',
  getCollectionComponentTypeById
);

export const addAllowedTypeAsync = createAsyncThunk(
  name + '/ addAllowedType',
  addAllowedType
);

export const getCollectionComponentTypesLookupsAsync = createAsyncThunk(
  name + '/ getCollectionComponentTypesLookups',
  getCollectionComponentTypesLookups
);

export const createCollectionComponentTypeAsync = createAsyncThunk(
  name + '/ createCollectionComponentType',
  createCollectionComponentType
);

export const getCollectionComponentTypesDatatableAsync = createAsyncThunk(
  name + '/ getCollectionComponentTypesDatatable',
  getCollectionComponentTypesDatatable
);

export const setAllowedComponentTypesAsync = createAsyncThunk(
  name + '/ setAllowedComponentTypes',
  setAllowedComponentTypes
);

export const getCollectionComponentTypeByIdMultilingualAsync = createAsyncThunk(
  name + '/ getCollectionComponentTypeByIdMultilingual',
  getCollectionComponentTypeByIdMultilingual
);

export const createCollectionComponentTypeMultilingualAsync = createAsyncThunk(
  name + '/ createCollectionComponentTypeMultilingual',
  createCollectionComponentTypeMultilingual
);

export const editCollectionComponentTypeMultilingualAsync = createAsyncThunk(
  name + '/ editCollectionComponentTypeMultilingual',
  editCollectionComponentTypeMultilingual
);

export const slice = createSlice({
  name: name,
  initialState,
  reducers: {
    clear: () => {
      return initialState;
    },
    clearCollectionComponentTypeById: (state) => {
      state.collectionComponentTypeById = {
        data: null,
        status: 'idle',
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCollectionComponentTypeByNameAsync.pending, (state) => {
        state.detailsStatus = 'loading';
      })
      .addCase(
        getCollectionComponentTypeByNameAsync.fulfilled,
        (state, action) => {
          state.detailsStatus = 'idle';
          state.details = action.payload;
        }
      )
      .addCase(getCollectionComponentTypeByNameAsync.rejected, (state) => {
        state.detailsStatus = 'failed';
      })
      .addCase(addAllowedTypeAsync.pending, (state) => {
        state.addAllowedType.status = 'loading';
      })
      .addCase(addAllowedTypeAsync.fulfilled, (state) => {
        state.addAllowedType = {
          status: 'idle',
        };
      })
      .addCase(addAllowedTypeAsync.rejected, (state) => {
        state.addAllowedType.status = 'failed';
      })
      .addCase(getCollectionComponentTypeByIdAsync.pending, (state) => {
        state.collectionComponentTypeById.status = 'loading';
      })
      .addCase(
        getCollectionComponentTypeByIdAsync.fulfilled,
        (state, action) => {
          state.collectionComponentTypeById = {
            data: action.payload,
            status: 'idle',
          };
        }
      )
      .addCase(getCollectionComponentTypeByIdAsync.rejected, (state) => {
        state.collectionComponentTypeById.status = 'failed';
      })
      .addCase(getCollectionComponentTypesLookupsAsync.pending, (state) => {
        state.collectionComponentTypesLookups.status = 'loading';
      })
      .addCase(
        getCollectionComponentTypesLookupsAsync.fulfilled,
        (state, action) => {
          state.collectionComponentTypesLookups = {
            data: action.payload,
            status: 'idle',
          };
        }
      )
      .addCase(getCollectionComponentTypesLookupsAsync.rejected, (state) => {
        state.collectionComponentTypesLookups.status = 'failed';
      })
      .addCase(createCollectionComponentTypeAsync.pending, (state) => {
        state.createCollectionComponentType.status = 'loading';
      })
      .addCase(
        createCollectionComponentTypeAsync.fulfilled,
        (state, action) => {
          state.createCollectionComponentType = {
            data: action.payload,
            status: 'idle',
          };
        }
      )
      .addCase(createCollectionComponentTypeAsync.rejected, (state) => {
        state.createCollectionComponentType.status = 'failed';
      })
      .addCase(getCollectionComponentTypesDatatableAsync.pending, (state) => {
        state.collectionComponentTypesDatatable.status = 'loading';
      })
      .addCase(
        getCollectionComponentTypesDatatableAsync.fulfilled,
        (state, action) => {
          state.collectionComponentTypesDatatable = {
            data: action.payload,
            status: 'idle',
          };
        }
      )
      .addCase(getCollectionComponentTypesDatatableAsync.rejected, (state) => {
        state.collectionComponentTypesDatatable.status = 'failed';
      })
      .addCase(setAllowedComponentTypesAsync.pending, (state) => {
        state.setAllowedComponentTypes.status = 'loading';
      })
      .addCase(setAllowedComponentTypesAsync.fulfilled, (state) => {
        state.setAllowedComponentTypes = {
          status: 'idle',
        };
      })
      .addCase(setAllowedComponentTypesAsync.rejected, (state) => {
        state.setAllowedComponentTypes.status = 'failed';
      })
      .addCase(
        getCollectionComponentTypeByIdMultilingualAsync.pending,
        (state) => {
          state.collectionComponentTypeByIdMultilingual.status = 'loading';
        }
      )
      .addCase(
        getCollectionComponentTypeByIdMultilingualAsync.fulfilled,
        (state, action) => {
          state.collectionComponentTypeByIdMultilingual = {
            data: action.payload,
            status: 'idle',
          };
        }
      )
      .addCase(
        getCollectionComponentTypeByIdMultilingualAsync.rejected,
        (state) => {
          state.collectionComponentTypeByIdMultilingual.status = 'failed';
        }
      )
      .addCase(
        createCollectionComponentTypeMultilingualAsync.pending,
        (state) => {
          state.createCollectionComponentTypeMultilingual.status = 'loading';
        }
      )
      .addCase(
        createCollectionComponentTypeMultilingualAsync.fulfilled,
        (state, action) => {
          state.createCollectionComponentTypeMultilingual = {
            data: action.payload,
            status: 'idle',
          };
        }
      )
      .addCase(
        createCollectionComponentTypeMultilingualAsync.rejected,
        (state) => {
          state.createCollectionComponentTypeMultilingual.status = 'failed';
        }
      )
      .addCase(
        editCollectionComponentTypeMultilingualAsync.pending,
        (state) => {
          state.editCollectionComponentTypeMultilingual.status = 'loading';
        }
      )
      .addCase(
        editCollectionComponentTypeMultilingualAsync.fulfilled,
        (state, action) => {
          state.editCollectionComponentTypeMultilingual = {
            data: action.payload,
            status: 'idle',
          };
        }
      )
      .addCase(
        editCollectionComponentTypeMultilingualAsync.rejected,
        (state) => {
          state.editCollectionComponentTypeMultilingual.status = 'failed';
        }
      );
  },
});

export const actions = slice.actions;

export default slice.reducer;
